/**
 * 🌱 Mowing Height Calculator
 * Provides optimal mowing height and frequency recommendations based on grass type and season
 */

import { GRASS_CHARACTERISTICS } from '../data/grass-characteristics'
import { GrassCharacteristics } from './types/recommendation-engine'
import {
  adjustMowingTargetForSunlight,
  attachToolEngineMetadata,
  buildCanonicalToolContext,
  type DeterministicToolResultMetadata,
} from '@/lib/lawn-plan-engine'

export interface MowingResult extends DeterministicToolResultMetadata {
  grassType: string
  grassSeason: 'Cool' | 'Warm'
  mowingHeight: {
    optimal: string
    minimum: string
    maximum: string
  }
  mowingFrequency: {
    growingSeason: string
    peakGrowth: string
    dormantSeason: string
  }
  currentSeason?: string
  currentRecommendations: {
    height: string
    frequency: string
    tips: string[]
  }
  generalTips: string[]
  seasonalTips: string[]
  grassCharacteristics: GrassCharacteristics
}

/**
 * Calculate mowing recommendations based on grass type and season
 */
export function calculateMowingRecommendations(
  grassType: string,
  currentSeason?: string,
  sunlight?: string | null,
): MowingResult {
  const context = buildCanonicalToolContext({
    toolId: 'mowing',
    grassType,
    sunlight,
  })
  // Get grass characteristics
  const grass = GRASS_CHARACTERISTICS[context.grassType]
  if (!grass) {
    throw new Error('Mowing data is not available for this grass type.')
  }

  // Get mowing details (fallback to basic mowingHeight if details not available)
  const mowingDetails = grass.mowingDetails || {
    optimal: grass.mowingHeight,
    minimum: getMinimumHeight(grass.mowingHeight),
    maximum: getMaximumHeight(grass.mowingHeight),
    frequency: {
      growingSeason: getDefaultFrequency(grass.season, 'growing'),
      peakGrowth: getDefaultFrequency(grass.season, 'peak'),
      dormantSeason: getDefaultFrequency(grass.season, 'dormant')
    },
    tips: getDefaultTips(grass.season)
  }

  // Determine current recommendations based on season
  const currentRecommendations = adjustCurrentRecommendationForSunlight(getCurrentRecommendations(
    mowingDetails,
    grass.season,
    currentSeason
  ), context.grassType, context.sunlight)

  // Generate seasonal tips
  const seasonalTips = generateSeasonalTips(grass.season, currentSeason)

  // Generate general tips
  const generalTips = [
    ...mowingDetails.tips,
    ...getGeneralMowingTips()
  ]

  return attachToolEngineMetadata({
    grassType: context.grassType,
    grassSeason: grass.season,
    mowingHeight: {
      optimal: mowingDetails.optimal,
      minimum: mowingDetails.minimum,
      maximum: mowingDetails.maximum
    },
    mowingFrequency: mowingDetails.frequency,
    currentSeason,
    currentRecommendations,
    generalTips,
    seasonalTips,
    grassCharacteristics: grass
  }, context)
}

function adjustCurrentRecommendationForSunlight<T extends { height: string; tips: string[] }>(
  recommendation: T,
  grassType: string,
  sunlight?: string | null,
): T {
  const target = parseHeightTarget(recommendation.height)
  if (target == null) return recommendation
  const adjusted = adjustMowingTargetForSunlight(target, grassType, sunlight as any)
  if (adjusted <= target) return recommendation

  return {
    ...recommendation,
    height: formatHeight(adjusted),
    tips: [
      ...recommendation.tips,
      'In shade, keep the canopy a little taller to protect photosynthesis.',
    ],
  }
}

function parseHeightTarget(raw: string): number | null {
  const values = raw.match(/\d+(?:\.\d+)?/g)?.map(Number).filter(Number.isFinite) ?? []
  if (values.length === 0) return null
  return Math.min(...values)
}

function formatHeight(value: number): string {
  return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)} inches`
}

/**
 * Get current recommendations based on season
 */
function getCurrentRecommendations(
  mowingDetails: any,
  grassSeason: 'Cool' | 'Warm',
  currentSeason?: string
) {
  if (!currentSeason) {
    return {
      height: mowingDetails.optimal,
      frequency: mowingDetails.frequency.growingSeason,
      tips: mowingDetails.tips.slice(0, 3) // Top 3 tips
    }
  }

  // Season-specific recommendations
  switch (currentSeason) {
    case 'spring':
      // Both seasons recommend optimal in spring. Pre-fix, warm-season returned
      // `minimum` which would have users scalping all spring — the "spring scalp"
      // is a one-time green-up event, not a season-long recommendation.
      return {
        height: mowingDetails.optimal,
        frequency: mowingDetails.frequency.growingSeason,
        tips: [
          ...mowingDetails.tips.slice(0, 2),
          grassSeason === 'Warm'
            ? 'One-time spring scalp clears dead growth, then return to optimal height'
            : 'Gradually adjust as growth accelerates',
          'Sharpen mower blades for clean cuts'
        ]
      }

    case 'summer':
      return {
        height: grassSeason === 'Cool' ? mowingDetails.maximum : mowingDetails.optimal,
        frequency: mowingDetails.frequency.peakGrowth,
        tips: [
          ...mowingDetails.tips.slice(0, 2),
          'Raise height during heat stress',
          'Mow early morning or late evening',
          'Keep grass longer to shade roots'
        ]
      }

    case 'fall':
      return {
        height: mowingDetails.optimal,
        frequency: mowingDetails.frequency.growingSeason,
        tips: [
          ...mowingDetails.tips.slice(0, 2),
          'Prepare for winter dormancy',
          'Reduce height gradually',
          'Remove leaves before mowing'
        ]
      }

    case 'winter':
      // Winter cool-season recommends OPTIMAL (or higher) height — taller grass
      // protects crowns from freeze-thaw and shades roots. Pre-fix this returned
      // `minimum`, which is horticulturally backwards: low mowing in winter is
      // exactly when cold damage is most likely.
      return {
        height: grassSeason === 'Cool' ? mowingDetails.optimal : 'Not applicable',
        frequency: grassSeason === 'Cool' ? mowingDetails.frequency.dormantSeason : 'Not needed',
        tips: [
          'Warm-season grasses are dormant',
          'Cool-season grasses grow slowly — keep height up for crown protection',
          'Mow only when necessary',
          'Keep equipment maintained'
        ]
      }

    default:
      return {
        height: mowingDetails.optimal,
        frequency: mowingDetails.frequency.growingSeason,
        tips: mowingDetails.tips.slice(0, 3)
      }
  }
}

/**
 * Generate seasonal tips
 */
function generateSeasonalTips(grassSeason: 'Cool' | 'Warm', currentSeason?: string) {
  const tips: string[] = []

  if (currentSeason) {
    switch (currentSeason) {
      case 'spring':
        tips.push(
          'Spring is prime growing season for cool-season grasses',
          'Warm-season grasses are beginning to green up',
          'Adjust mowing frequency as growth accelerates'
        )
        break
      case 'summer':
        tips.push(
          'Cool-season grasses need stress protection',
          'Warm-season grasses are in peak growing season',
          'Increase mowing frequency during rapid growth'
        )
        break
      case 'fall':
        tips.push(
          'Fall is optimal seeding time for cool-season grasses',
          'Warm-season grasses are preparing for dormancy',
          'Gradually adjust height for winter preparation'
        )
        break
      case 'winter':
        tips.push(
          'Cool-season grasses grow slowly in winter',
          'Warm-season grasses are completely dormant',
          'Minimize mowing to avoid damage'
        )
        break
    }
  }

  return tips
}

/**
 * Get minimum height from range string
 */
function getMinimumHeight(heightRange: string): string {
  const match = heightRange.match(/(\d+(?:\.\d+)?)/)
  if (match) {
    const min = parseFloat(match[1])
    return `${(min - 0.5).toFixed(1)} inches`
  }
  return '2 inches'
}

/**
 * Get maximum height from range string
 */
function getMaximumHeight(heightRange: string): string {
  const match = heightRange.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/)
  if (match) {
    const max = parseFloat(match[2])
    return `${(max + 0.5).toFixed(1)} inches`
  }
  return '4 inches'
}

/**
 * Get default frequency based on grass season and period
 */
function getDefaultFrequency(
  season: 'Cool' | 'Warm',
  period: 'growing' | 'peak' | 'dormant'
): string {
  if (season === 'Cool') {
    switch (period) {
      case 'growing': return 'Weekly'
      case 'peak': return 'Every 5-7 days'
      case 'dormant': return 'Bi-weekly or as needed'
    }
  } else {
    switch (period) {
      case 'growing': return 'Every 5-7 days'
      case 'peak': return 'Every 3-5 days'
      case 'dormant': return 'Not needed (dormant)'
    }
  }
}

/**
 * Get default tips based on grass season
 */
function getDefaultTips(season: 'Cool' | 'Warm'): string[] {
  if (season === 'Cool') {
    return [
      'Never remove more than 1/3 of grass blade at once',
      'Keep blades sharp for clean cuts',
      'Raise height during summer stress periods'
    ]
  } else {
    return [
      'Can be mowed lower than cool-season grasses',
      'Frequent mowing encourages lateral growth',
      'Raise height during drought stress'
    ]
  }
}

/**
 * Get general mowing tips
 */
function getGeneralMowingTips(): string[] {
  return [
    'Always use sharp mower blades',
    'Mow when grass is dry',
    'Vary mowing pattern to prevent soil compaction',
    'Leave grass clippings on lawn (they return nutrients)',
    'Adjust mowing height based on weather conditions'
  ]
}
